import { useRef } from 'react';
import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { useMy } from '@/hooks/useMy';
import { useAuth } from '@/state/auth';
import { Setting } from '@/types';
import { useMutation } from '@/utils/api';

/**
 * Update setting with automatic success toast
 *
 * Default behavior: Shows "Setting updated successfully" toast
 * Override: Pass { silent: true } in variables to suppress
 *
 * Note: Originally used hideSuccessToast, now migrated to silent pattern
 * for consistency with other mutation hooks.
 *
 * Error handling: Components should handle errors in catch blocks.
 * The onError callback was removed to prevent double-toast bugs.
 *
 * Cache invalidation: Uses a ref to always access the latest invalidate
 * function, avoiding stale closure issues when `me` changes after initial
 * auth loading. The query key always includes the effective email to match
 * the fetch query key used by consuming components.
 *
 * @example
 * // Standard usage (automatic success toast)
 * const { mutate } = useUpdateSetting();
 * mutate({ name: 'setting-name', value: 'new-value' });
 *
 * @example
 * // Silent update (no toast, component handles success/error)
 * mutate({ name: 'setting-name', value: 'new-value', silent: true });
 */
export const useUpdateSetting = (customerEmail?: string) => {
  const { me, friend } = useAuth();
  // Use customerEmail if provided (impersonation), otherwise fall back to current user
  // This ensures the invalidate query key matches the fetch query key
  const effectiveEmail = customerEmail || friend || me;

  const axios = useAxios({
    email: customerEmail,
    doNotImpersonate: !!customerEmail,
  });

  const { invalidate } = useMy(
    { resource: 'setting' },
    { email: effectiveEmail, enabled: false }
  );

  // Use a ref to always get the latest invalidate function
  // This avoids stale closure bugs when me/friend change after auth loading
  const invalidateRef = useRef(invalidate);
  invalidateRef.current = invalidate;

  return useMutation({
    defaultErrorMessage: 'Failed to update setting',
    getErrorFromResponse: true,
    mutationFn: async ({
      name,
      value,
      silent,
    }: {
      name: string;
      value: Setting['value'];
      silent?: boolean;
    }) => {
      await axios.put('/setting', { name, value });

      // Use ref to call the latest invalidate function (avoids stale closure)
      invalidateRef.current();

      // Show success toast unless suppressed
      if (!silent) {
        toast.success('Setting updated successfully');
      }
    },
    // onError removed: Components should handle errors in catch blocks
    // to prevent double-toast bugs and provide context-specific messages
  });
};
